<template>
    <div class="wrapper">
        <img src="logo.svg" width="300" class="logo"/>
        <div>
            <section id="stores">
                <h2 style="margin-bottom:0;">{{ ready ? "Select your Store" : "Loading stores" }}</h2>

                <div class="sites">
                    <div class="site" v-for="site in sites" @click="selectSite(site)" :key="'site-'+site.id">
                        {{ site.name }}

                        <div class="reset-button" @click.stop.prevent="resetStoreId=site.id" v-if="site.canReset">
                            <img src="../assets/reset-update.svg"/>
                        </div>
                    </div>
                </div>
                <h2 v-if="ready && sites.length === 0">
                    No stores available for your user.
                </h2>
            </section>

            <store-reset @close="resetStoreId = undefined;" v-if="resetStoreId" :storeId="resetStoreId"
                         :storeName="storeName"></store-reset>

            <h2>Select your template(s)</h2>
            <section id="settings" v-if="ready">
                <div class="settings-wrapper">
                    <div class="setting title">
                        <label class="hidden"></label>
                        <h3 class="input">Social Distance</h3>
                    </div>
                    <div v-for="locale in $i18n.availableLocales" :key="locale" class="setting">
                        <label :key="locale+'-label'">
                            {{ locale }} <input type="checkbox" @change.prevent.stop="toggleLocale(locale)"
                                                :checked="selectedIndex(locale) !== -1" :value="locale"
                                                data-lpignore="true"
                                                v-model="checkedLocales">
                        </label>

                        <input type="text" placeholder="2 meter" v-if="selectedIndex(locale) !== -1"
                               data-lpignore="true"
                               :key="locale+'-distance'" v-model="selectedLocales[selectedIndex(locale)].distance"/>
                    </div>
                </div>
                <div class="settings-wrapper">
                    <div class="setting title">
                        <h3 class="input">Store name</h3>
                    </div>
                    <div class="setting">
                        <input type="text" v-model="storeIdentifier" data-lpignore="true"/>
                    </div>
                </div>
            </section>
        </div>
        <br/>
        <button class="sign-out-button" @click="signOut">Sign out</button>
        <loading-icon v-if="!ready"></loading-icon>
    </div>
</template>

<script>
import axios from 'axios';
import LoadingIcon from '../components/LoadingIcon';
import StoreReset from '../components/CustomerFacing/store-reset';

export default {
  name: 'SiteSelect',
  components: {StoreReset, LoadingIcon},

  data() {
    return {
      ready: false,
      sites: [],
      checkedLocales: [],
      selectedLocales: [],
      storeIdentifier: this.$store.getters['storeName'],
      resetStoreId: false,
      selecting: false
    };
  },

  computed: {
    canClick() {
      let clickable = this.selectedLocales.length > 0 ? true : false;

      for (let i = 0; i < this.selectedLocales.length; i++) {
        if ([undefined, ''].includes(this.selectedLocales[i].distance)) {
          clickable = false;
          break;
        }
      }

      return clickable;
    },
    storeName() {
      if (this.resetStoreId) {
        return this.sites.find((s) => s.id === this.resetStoreId).name;
      }
      return undefined;
    },
  },

  mounted() {
    this.selectedLocales = this.$store.getters['i18n/selected'];
    this.selectedLocales.forEach(l => this.checkedLocales.push(l.locale));
    this.fetchSites();
  },

  methods: {
    fetchSites() {
      axios.get(process.env.VUE_APP_API_BASE + `/sites?api_key=${this.$store.getters.key}&fields=id,name,customer,user_dashboard_features,analytics_dashboard_start_date&limit=1000`)
        .then((res) => {
          // Filter sites based on available feature 'occupancy'
          this.sites = res.data.results.filter(s => s.user_dashboard_features.includes('occupancy'));
          this.ready = true;
        })
        .then(() => {
          // Fetch admin rights
          this.sites.forEach(s => {
            this.canResetStore(s);
          });
        });
    },

    selectSite(site) {
      if (!this.selecting) {
        if (this.canClick) {
          this.selecting = true;

          // Store selected name;
          this.$store.dispatch('SET_STORE_NAME', this.storeIdentifier);
          // Store selected locales;
          this.$store.dispatch('i18n/SET', this.selectedLocales)
            .then(() => {
              this.$router.push({name: 'store', params: {id: site.id}});
            });
        } else {
          this.$notify.error('Language selection not complete');
          this.selecting = false;
        }
      }
    },

    signOut() {
      localStorage.removeItem(process.env.VUE_APP_COOKIE_NAME);
      window.location = '/';
    },

    selectedIndex(locale) {
      return this.selectedLocales.findIndex((l) => {
        return l.locale === locale.toLowerCase();
      });
    },

    toggleLocale(selected) {
      const index = this.selectedIndex(selected);
      if (index === -1) {
        this.selectedLocales.push({locale: selected, distance: ''});
      } else {
        if (this.selectedLocales.length > 1) {
          this.selectedLocales.splice(index, 1);
        } else {
          this.$nextTick(() => {
            this.checkedLocales.push(this.selectedLocales[0].locale);
          });
          this.$notify.error('At least 1 language should be selected');
        }
      }
    },

    canResetStore(site) {
      axios.get(`${process.env.VUE_APP_API_BASE}/user-customer-features?customer_id=${site.customer}&api_key=${this.$store.getters['key']}`)
        .then((res) => {
          let x = res.data.find(feature => feature.slug === 'occupancy-admin');
          this.$set(site, 'canReset', x ? true : false);
        });
    }
  }
};
</script>

<style scoped lang="scss">

.wrapper {
    height: 100%;
    width: 100%;
    padding: 2rem;
    display: flex;
    flex-flow: column;
    align-items: center;
}

.logo {
    margin-bottom: 2rem;
    max-width: 300px;
}

.sites {
    margin: 1em auto 3em;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;

    .site {
        padding: 2rem;
        border: 4px solid #4156A7;
        text-align: center;
        cursor: pointer;
        display: flex;
        flex-flow: column;
        justify-content: center;
        position: relative;

        .reset-button {
            position: absolute;
            right: 0;
            top: 0;
            padding: .5em;
            background: rgba(0, 0, 0, 0.1);
            border-bottom-left-radius: 5px;
            display: flex;
            opacity: 0.5;
            transitioN: opacity .1s ease-in-out;

            img {
                width: 16px;
                height: 16px;
            }

            &:hover {
                opacity: 1;
            }
        }
    }
}

section#settings {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;

    .settings-wrapper {
        text-align: left;

        .setting {
            width: 100%;
            height: 2.5em;
            margin-bottom: .5em;
            display: flex;
            align-items: center;
            box-sizing: border-box;

            label {
                width: 90px;
                border: 4px solid $brand-primary;
                height: inherit;
                margin: .5rem .5rem .5rem 0;
                display: flex;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;

                &.hidden {
                    visibility: hidden;
                }

                input {
                    height: auto;
                    max-width: 30px;
                }
            }

            input, .input {
                flex: 2;
                box-sizing: border-box;
                text-align: left;
                height: 100%;
            }

            &.title {
                height: 1em;
            }
        }
    }
}

.sign-out-button {
    padding: .75rem 1.25rem;
    margin: .5rem;
    border: 4px solid $brand-primary;
    background-color: $brand-primary;
    color: #FFF;
    font-weight: bold;
    cursor: pointer;
    border-radius: 0;
}

h3 {
    margin: 0;
}

</style>